import { useState } from 'react'

import startCase from 'lodash/startCase'

import { hasActionPermission } from '@tribeplatform/gql-client'
import {
  CustomSsoType,
  Sso,
  SsoProvider,
  SsoStatus,
  SsoType,
} from '@tribeplatform/gql-client/types'
import { Form } from '@tribeplatform/react-sdk/components'
import { useNetwork, useUpdateCustomSso } from '@tribeplatform/react-sdk/hooks'
import { ActionPanel } from '@tribeplatform/react-ui-kit/ActionPanel'
import { Alert } from '@tribeplatform/react-ui-kit/Alert'
import { Button } from '@tribeplatform/react-ui-kit/Button'
import { Card } from '@tribeplatform/react-ui-kit/Card'
import { Link } from '@tribeplatform/react-ui-kit/Link'
import { toast } from '@tribeplatform/react-ui-kit/Toast'

import { URL_DETECTOR } from '../../../../lib/validator.utils'
import { PlanBadge } from '../../../PlanBadge'

const getProviderName = (provider: SsoProvider) =>
  startCase(provider).replace(' ', '')

const providers = Object.values(SsoProvider).map(provider => ({
  text: getProviderName(provider),
  value: provider,
}))

export const CustomSso = ({ sso: _sso }: { sso: Sso }) => {
  const { data: network } = useNetwork()
  const [provider, setProvider] = useState<SsoProvider>(null)
  const [expanded, setExpanded] = useState(false)
  const { mutateAsync: updateCustomSso, isLoading } = useUpdateCustomSso()

  const isCustom = provider === SsoProvider.CUSTOM
  const idpUrlLabel = isCustom
    ? `Identity Provider URL`
    : `${getProviderName(provider)} Domain`

  let sso = _sso
  if (!sso) {
    sso = {
      status: SsoStatus.DISABLE,
      type: SsoType.OAUTH2,
      provider: SsoProvider.AUTH0,
    }
  }

  const { authorized, requiredPlan } = hasActionPermission(
    network.authMemberProps?.permissions ?? [],
    'updateCustomSso',
  )

  if (!provider && sso.provider) setProvider(sso.provider)

  const description = (
    <>
      Allow users to register and access the community using their existing
      credentials on your website or application.{' '}
      <Link
        href="https://community.tribe.so/knowledge-base-2-0/post/oauth2-sso-wsQUqc2rI43ulRi"
        external
      >
        Learn more
      </Link>
    </>
  )

  if (!expanded) {
    return (
      <ActionPanel
        title={
          <div className="flex items-center space-x-2">
            <div>Single sign-on</div>
            <div>
              <PlanBadge
                requiredPlanName={requiredPlan}
                authorized={authorized}
              />
            </div>
          </div>
        }
        placement="trailing-top"
        description={
          sso?.status === SsoStatus.ENABLE
            ? `${getProviderName(sso.provider)} sso is enabled.`
            : description
        }
      >
        <Button
          variant="outline"
          size="lg"
          onClick={() => setExpanded(true)}
          disabled={!authorized}
        >
          {sso?.status === SsoStatus.ENABLE ? 'Change settings' : 'Enable'}
        </Button>
      </ActionPanel>
    )
  }

  return (
    <Card>
      <Card.Header title="Single sign-on" description={description} />
      <Card.Content>
        <Form
          defaultValues={{
            ...sso,
            enable: sso.status === SsoStatus.ENABLE,
            scopes: sso.scopes?.join(',') ?? '',
          }}
          onSubmit={data => {
            const scopesArray = data?.scopes
              ? data.scopes.trim().replace(/,$/gi, '').split(',')
              : []
            return updateCustomSso({
              input: {
                provider: data.provider,
                authorizationUrl: data.authorizationUrl,
                buttonText: data.buttonText,
                clientId: data.clientId,
                clientSecret: data.clientSecret,
                idpUrl: data.idpUrl,
                logoutUrl: data.logoutUrl,
                settingsUrl: data.settingsUrl,
                tokenUrl: data.tokenUrl,
                userProfileUrl: data.userProfileUrl,
                scopes: scopesArray,
                status: data?.enable ? SsoStatus.ENABLE : SsoStatus.DISABLE,
                type: CustomSsoType.OAUTH2,
                name: data?.provider,
              },
            }).then(() => {
              toast({
                title: 'OAuth2 settings updated',
                status: 'success',
              })
            })
          }}
          className="flex flex-col space-y-5"
        >
          <Form.Select
            label="SSO Provider"
            placeholder="Select a provider"
            name="provider"
            items={providers}
            onChange={setProvider}
          />
          <Form.Input label="Client ID" name="clientId" />
          <Form.Input label="Client Secret" name="clientSecret" />
          <Form.Input
            label={idpUrlLabel}
            name="idpUrl"
            validation={{
              pattern: {
                value: URL_DETECTOR,
                message: 'Invalid url format',
              },
            }}
          />
          {isCustom && (
            <>
              <Form.Input
                label="Authorization URL"
                name="authorizationUrl"
                validation={{
                  pattern: {
                    value: URL_DETECTOR,
                    message: 'Invalid url format',
                  },
                }}
              />
              <Form.Input
                label="Token URL"
                name="tokenUrl"
                validation={{
                  pattern: {
                    value: URL_DETECTOR,
                    message: 'Invalid url format',
                  },
                }}
              />
              <Form.Input
                label="User Profile URL"
                name="userProfileUrl"
                validation={{
                  pattern: {
                    value: URL_DETECTOR,
                    message: 'Invalid url format',
                  },
                }}
              />
              <Form.Input label="Scopes (Comma Separated)" name="scopes" />
              <Form.Input
                label="Settings Url"
                name="settingsUrl"
                validation={{
                  pattern: {
                    value: URL_DETECTOR,
                    message: 'Invalid url format',
                  },
                }}
              />
              <Form.Input
                label="Logout Url"
                name="logoutUrl"
                validation={{
                  pattern: {
                    value: URL_DETECTOR,
                    message: 'Invalid url format',
                  },
                }}
              />
            </>
          )}
          <Form.Input label="Login Button Text" name="buttonText" />
          <Alert status="info">
            Please use https://{network.domain}/ssos/redirect as the callback
            url.
          </Alert>
          <div className="flex justify-between items-center">
            <Form.Toggle label="Enable OAuth2" name="enable" className="mb-2" />
            <div className="flex space-x-3">
              <Button variant="outline" onClick={() => setExpanded(false)}>
                Close
              </Button>
              <Button variant="primary" type="submit" loading={isLoading}>
                Update
              </Button>
            </div>
          </div>
        </Form>
      </Card.Content>
    </Card>
  )
}
